import { useState, useEffect, useRef, type FormEvent } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { trackLead } from "@/lib/meta-pixel";

const emailSchema = z
  .string()
  .trim()
  .email({ message: "Invalid email address" })
  .max(255, { message: "Email must be less than 255 characters" });

type Props = {
  placeholder: string;
  ctaLabel: string;
  className?: string;
  successMessage?: string;
  submittingLabel?: string;
  genericErrorMessage?: string;
  /** Cloudflare Turnstile sitekey; when set, an invisible captcha token is sent with the email. */
  siteKey?: string;
  onSubmitEmail: (
    email: string,
    browserLanguage: string,
    captchaToken: string,
  ) => Promise<{ ok: true } | { ok: false; error?: string }>;
};

function getBrowserLanguage(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.languages?.[0] || navigator.language || "";
}

export function JoinWaitlistForm({
  placeholder,
  ctaLabel,
  className,
  successMessage,
  submittingLabel,
  genericErrorMessage,
  siteKey,
  onSubmitEmail,
}: Props) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState("");
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Render an invisible Turnstile widget once its script has loaded. It runs in
  // the background and hands us a token via the callback — no interaction for
  // legitimate visitors (appearance: interaction-only). No cf-turnstile class:
  // that would trigger a second, implicit auto-render.
  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled || !window.turnstile || !captchaRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: siteKey,
        size: "flexible",
        appearance: "interaction-only",
        callback: (t: string) => setToken(t),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
    };
    const id = window.setInterval(tryRender, 200);
    tryRender();
    return () => {
      cancelled = true;
      window.clearInterval(id);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? "Invalid email address");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await onSubmitEmail(result.data, getBrowserLanguage(), token);
      if (res.ok) {
        setDone(true);
        trackLead();
      } else {
        setError(res.error || genericErrorMessage || "Something went wrong. Please try again.");
        // Turnstile tokens are single-use — reset for a fresh one on retry.
        setToken("");
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch {}
        }
      }
    } catch {
      setError(genericErrorMessage || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <p className={`text-p2 text-text-secondary ${className ?? ""}`}>
        {successMessage ?? "Thanks — you're on the list."}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className={className}>
      <div className="flex w-full max-w-lg flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <input
          type="email"
          required
          inputMode="email"
          autoComplete="email"
          maxLength={255}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-invalid={!!error}
          className="h-12 w-full rounded-full border border-border bg-[var(--background-main)] px-5 text-[16px] leading-6 text-text-primary placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-w-0 sm:flex-1"
        />
        <Button
          type="submit"
          variant="mainPrimary"
          size="main"
          disabled={submitting}
          className="h-12 shrink-0 rounded-full"
        >
          {submitting ? submittingLabel || `${ctaLabel}…` : ctaLabel}
        </Button>
      </div>
      {error && (
        <p role="alert" className="mt-2 px-1 text-[13px] leading-5 text-destructive">
          {error}
        </p>
      )}
      {/* Invisible for legit visitors; when Turnstile needs interaction the widget
          renders here — give it breathing room and align it to the input row. */}
      {siteKey && <div ref={captchaRef} className="mt-3 w-full max-w-lg [&:empty]:hidden" />}
    </form>
  );
}
